import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../../interceptors";

// Bulk CSV exports for candidates and interviews. Uses blob response so the
// browser triggers a proper download with a timestamped filename.

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { delay: i * 0.12, duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Exports = () => {
  const [busy, setBusy] = useState(null);

  useEffect(() => { document.title = "Exports - Ample Hire"; }, []);
  const [toast, setToast] = useState(null);

  const download = async (kind) => {
    setBusy(kind);
    setToast(null);
    try {
      const url =
        kind === "candidates"
          ? "/job-posting/export/candidates"
          : "/job-posting/export/interviews";
      const res = await axiosInstance.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const objUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = objUrl;
      a.download = `ample-hire-${kind}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objUrl);
      setToast({ kind: "ok", msg: `${kind === "candidates" ? "Candidates" : "Interviews"} CSV downloaded.` });
    } catch (e) {
      setToast({ kind: "err", msg: "Export failed. Please try again." });
    } finally {
      setBusy(null);
      setTimeout(() => setToast(null), 3500);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 font-body">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className="text-2xl font-bold text-vf-primary font-heading">
          Exports
        </h1>
        <p className="text-sm mt-1 text-vf-muted">
          Download CSV snapshots of your candidate pool and interview sessions.
        </p>
      </motion.div>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            className={`rounded-xl px-4 py-3 text-sm ${
              toast.kind === "ok" ? "badge-success" : "badge-danger"
            }`}
          >
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ExportCard
          index={0}
          title="All candidates"
          description="Every parsed candidate in your library: name, email, role, skills, experience, location."
          filename="ample-hire-candidates-YYYY-MM-DD.csv"
          busy={busy === "candidates"}
          onDownload={() => download("candidates")}
        />
        <ExportCard
          index={1}
          title="All interviews"
          description="Every interview session: candidate, job, status, overall score, feedback tag, timestamps."
          filename="ample-hire-interviews-YYYY-MM-DD.csv"
          busy={busy === "interviews"}
          onDownload={() => download("interviews")}
        />
      </div>
    </div>
  );
};

const ExportCard = ({ index, title, description, filename, busy, onDownload }) => {
  return (
    <motion.div
      custom={index}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      className="card p-6 border border-transparent hover:border-vf-primary transition-colors"
    >
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-vf-primary text-white mb-4">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      </div>
      <h3 className="text-lg font-bold text-vf-base font-heading">{title}</h3>
      <p className="text-sm mt-1 text-vf-muted">{description}</p>
      <div className="text-xs mt-3 font-mono text-vf-muted">{filename}</div>
      <motion.button
        onClick={onDownload}
        disabled={busy}
        className="btn-primary w-full mt-4 relative overflow-hidden"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <AnimatePresence mode="wait">
          {busy ? (
            <motion.span
              key="loading"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="inline-flex items-center gap-2"
            >
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
              />
              Preparing...
            </motion.span>
          ) : (
            <motion.span
              key="ready"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              Download CSV
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </motion.div>
  );
};

export default Exports;
